import { Application, Container, Target } from '@tencent/alloy-renderer';
import { EventArea } from '@tencent/alloy-editor';
import { isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import i18n from '@i18next/i18n';
import EmptyModel from '@src/slide/domain/ui-model/element-modeles/empty-model';
import { reaction } from 'mobx';
import {
    IEditorProps,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import {
    getPlaceholderParagraphList,
    styleRenderCallback,
    LINE_HEIGHT,
} from './common-note-data';
import { TouchHandler } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/common/touch-handler';
import { NOTE_COLLAPSE_HEIGHT } from '@editor/src/js/constants';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { getSlideIndex } from '@src/slide/domain/ui-model/element-modeles/property/slide-number/operation';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { isPortrait } from 'utils';
import Snackbar from '@tencent/dui-mobile/lib/components/Snackbar';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import Task from '@src/slide/infrastructure/module/dependent/taskManager/task';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IMobileRightMenuMananger } from '@src/slide/application/service/mobile-right-menu-manager/core/index.interface';
import { IInfrastructureBase } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import { IEditorServices } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/interface/editor-service.interface';
import { NoteEditLogicService } from '@src/slide/content-viewer/service/canvas-renderer/entry/edit-note/logic/note-edit-logic-service';
import { TextEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor';

const SHOW_RIGHT_MENU_DELAY = 200;
const MAX_NOTE_HEIGHT = Math.max(document.body.clientHeight, document.body.clientWidth) + 100;
const NOTE_DELAY_RENDER_KEY = 'note_delay_render_task';

export interface INoteAreaProps {
    application: Application;
    noteBodyStyle: any;
    placeholderBodyStyle: any;
    editMode: boolean;
    isScrollMode: boolean;
    canvasElement: HTMLCanvasElement;
    containerElement: HTMLDivElement;
    onEnterEditing?: () => void;
    onExitEditing?: () => void;
    onDoubleTap?: () => void;
    handleCurrentSlideChangeStart?: () => void;
    handleCurrentSlideChangeEnd?: () => void;
    getTextAreaElemHeight?: () => number;
    handleNoteHeightUpdated?: (textRectHeight: number, applicationHeight: number) => void,
}

export class NoteArea extends Container {
    public editMode: boolean;
    public containerHeight: number;
    public isLargeNote: boolean;
    public offsetTop: number;

    private readonly application: Application<Target>;
    private reactionHandler: any;
    private textEditor: TextEditor | null;
    private model: any;
    private slide: any;
    private isFocus: boolean;
    private placeHolderComponent: TextEditor | null;
    private cacheSelectionRect: {
        top: number,
        bottom: number,
    };
    private reactionSlideLengthHandler: any;
    private props: INoteAreaProps;
    private touchHandler: TouchHandler;
    private selectionTimeout: any;
    private mobileRightMenuManager: IMobileRightMenuMananger;
    private textTouchHandler: TouchHandler;
    private taskId: number;
    private isShowTip = false;

    public constructor(props: INoteAreaProps) {
        super();
        this.props = props;
        this.application = props.application;
        this.isFocus = false;
        this.offsetTop = 0;
        this.editMode = props.editMode;
        this.containerHeight = NOTE_COLLAPSE_HEIGHT;
        this.updateArea();
        this.addObserver();
        this.addSlideObserver();
        this.cacheSelectionRect = {
            top: 0,
            bottom: 0,
        };
        this.touchHandler = new TouchHandler(this, {
            preventDefaultConfig: {
                touchend: true,
            },
            doubleTap: () => {
                this.handleNoteDoubleTap();
                this.showMobileRightMenu();
            },
        });
        this.getAppServices().viewableServiceLayer.mobileRightMenuMananger.promise().then((mobileRightMenuMananger) => {
            this.mobileRightMenuManager = mobileRightMenuMananger;
        });
        this.taskId = 0;
    }

    public dispose(): void {
        this.model.noteInstance?.textEditor?.triggerBlur();
        super.dispose();
        this.removeSlideObserver();
        this.removeObserver();
        this.touchHandler.dispose();
        this.textTouchHandler?.dispose();
    }

    public updateEditorPropsFromSizeChange(delayRender: boolean): void {
        if (browser.isIOS && delayRender) {
            // ios在持续滚动页面时退出全屏会导致备注渲染失败，因此延迟更新
            setTimeout(() => this.activeUpdateEditorPropsFromSizeChange(), 100);
        } else {
            this.activeUpdateEditorPropsFromSizeChange();
        }
    }
    public activeUpdateEditorPropsFromSizeChange(): void {
        if (!this.isShowTip && this.editMode && !isPortrait() && browser.isMobile && !browser.isPad) {
            this.isShowTip = true;
            Snackbar.show({
                message: i18n.t('为了你的更好体验，建议在竖屏下编辑备注。'),
                duration: 2000,
            });
            // 由于 Snackbar 自动关闭时没有回调，所以使用定时器重置 tip 显示状态，定时器时间和 duration 保持一致即可
            setTimeout(() => {
                this.isShowTip = false;
            }, 2000);
        }
        this.updateArea();
    }

    public renderNoteFromKayBoardChange(): void {
        const containerElem = this.getContainerElem();
        if (!containerElem) return;
        const canvasRect = this.props.canvasElement?.getBoundingClientRect();
        const containerRect = containerElem.getBoundingClientRect();
        const canvasBottom = Math.round(canvasRect?.bottom || 0);
        const containerBottom = Math.round(containerRect?.bottom || 0);
        const canvasTop = Math.round(canvasRect?.top || 0);
        const containerTop = Math.round(containerRect?.top || 0);
        if (canvasBottom < containerBottom) {
            // 在底部额外留1px的空间，使得可以继续向下滚动
            containerElem.scrollTop = Math.max(containerElem.scrollTop - (containerBottom - canvasBottom + 1), 0);
        }
        if (canvasTop > containerTop) {
            containerElem.scrollTop = Math.max(containerElem.scrollTop - (canvasTop - containerTop), 0);
        }
    }

    public updateEditorPropsWithoutValueChange(): void {
        const props = this.getEditorProps(false);
        this.textEditor?.updateEditorProps(props);
    }

    public triggerBlur(): void {
        if (this.isFocus) {
            this.model.noteInstance?.textEditor?.triggerBlur();
            this.isFocus = false;
            this.updateArea();
        }
    }

    public scrollContainerIfHeightChange = (textRectHeight: number): void => {
        if (!this.textEditor) {
            return;
        }
        const applicationHeight = this.application.view.height;
        if (textRectHeight <= this.containerHeight && applicationHeight <= this.containerHeight) {
            return;
        }
        if (textRectHeight === this.props.getTextAreaElemHeight?.()) {
            return;
        }
        this.updateContainerScroll();
    };

    public scrollToView = () => {
        this.updateContainerScroll(true);
    };

    public slideIndexHasChange = (): boolean => this.model !== this.getAppServices().viewableServiceLayer
        .selectionService.currentSlide?.noteData;

    public isShowPlaceHolder = () => {
        if (!this.slideExistence()) return true;
        if (this.isFocus || !this.isEditable() || this.editMode) return false;
        return isEditorEmpty(this.model.noteTextBody?.paragraphList || []);
    };

    public setFocus = (): void => this.textEditor?.setFocus();

    public getTextAreaHeightSync = (): number => this.textEditor?.getTextAreaHeightSync() || 0;

    public updateTextEditorProps = (): void => {
        this.textEditor?.updateEditorProps(this.getEditorProps(true));
    };

    public updateCacheSelectionRect = (): void => {
        if (this.cacheSelectionRect.top) {
            const newSelectionRect = this.textEditor?.getExpandedRect();
            const top = Math.floor(newSelectionRect?.top || 0);
            const bottom = Math.floor(newSelectionRect?.bottom || 0);
            this.cacheSelectionRect = { top, bottom };
        }
    };

    public updateApplicationSize = (width: number, height: number): void => {
        this.setApplicationWidth(width);
        this.setApplicationHeight(height);
    };

    private addSlideObserver() {
        this.reactionHandler = reaction(
            () => this.getAppServices().viewableServiceLayer.selectionService.currentSlide,
            () => {
                this.props.handleCurrentSlideChangeStart?.();
                if (!this.getAppServices().viewableServiceLayer.noteAreaManager.show) {
                    this.removeObserver();
                    this.updateModel();
                    this.addObserver();
                    return;
                }
                this.triggerBlur();
                this.removeObserver();
                this.updateArea(true);
                this.addObserver();
                this.updateEditorInstance();
                this.props.handleCurrentSlideChangeEnd?.();
            },
        );
        this.reactionSlideLengthHandler = reaction(
            () => this.getAppServices().viewableServiceLayer.selectionService.slides.length,
            () => {
                if (this.getAppServices().viewableServiceLayer.noteAreaManager.show) {
                    this.updateArea();
                }
            },
        );
    }

    private removeSlideObserver() {
        this.reactionHandler();
        this.reactionSlideLengthHandler();
    }

    private addObserver() {
        if (this.slide?.changeSetEngine) {
            this.slide.changeSetEngine.addObserveForChangeAtPath('noteSlideId', this.updateArea);
        }
        if (this.model.noteTextBody?.changeSetEngine) {
            this.model.noteTextBody.changeSetEngine.addChangesetObserver(this.updateArea);
        }
    }

    private removeObserver() {
        if (this.slide?.changeSetEngine) {
            this.slide.changeSetEngine.removeObserveForChangeAtPath('noteSlideId', this.updateArea);
        }
        if (this.model.noteTextBody?.changeSetEngine) {
            this.model.noteTextBody.changeSetEngine.removeChangesetObserver(this.updateArea);
        }
    }

    private updateArea = (updateDelay?: boolean) =>  {
        this.updateModel();
        updateDelay === true ? this.delayRenderNote() : this.renderNote();
    };

    private updateModel = () => {
        this.model = this.getAppServices().viewableServiceLayer.selectionService.currentSlide?.noteData
        || new EmptyModel({});
        this.slide = this.getAppServices().viewableServiceLayer.selectionService.currentSlide;
    };

    private delayRenderNote = () => {
        // 备注渲染任务只保留最新的一个
        if (TaskManager.getTaskWithKey(NOTE_DELAY_RENDER_KEY + this.taskId)) {
            TaskManager.cancelTaskByKey(NOTE_DELAY_RENDER_KEY + this.taskId);
        }
        this.taskId = this.taskId + 1;
        const task = new Task(NOTE_DELAY_RENDER_KEY + this.taskId, TaskPriority.LOW, () => {
            this.renderNote();
        });
        // 移除切页前的备注，等异步加载新的数据
        this.removeLastEditor();
        TaskManager.addTask(task);
    };

    private renderNote = () => {
        // 文本框
        this.renderTextEditor();
        // 占位符
        this.renderPlaceholder();
    };

    private removeLastEditor = () => {
        if (this.textEditor) {
            // 移除现有的备注
            this.textEditor.editorContainer.dispose();
            this.removeChild(this.textEditor.editorContainer);
            this.textEditor = null;
        }
        if (!this.isShowPlaceHolder() && this.placeHolderComponent) {
            // 如果切页后备注有内容，则把现有的 placeHolder 也移除
            this.placeHolderComponent.editorContainer.dispose();
            this.removeChild(this.placeHolderComponent.editorContainer);
            this.placeHolderComponent = null;
        }
    };

    private isEditable = () => this.model?.currentEditState === PPTEditorStateType.EDITABLE;
    private slideExistence = () => this.getAppServices().viewableServiceLayer.selectionService.currentSlideIndex !== -1;

    private updateEditorInstance = () => {
        this.model.noteTextBody?.setEditorComponent(this.textEditor);
    };

    private renderTextEditor = () => {
        if (this.isShowPlaceHolder()) {
            if (this.textEditor) {
                this.textEditor.editorContainer.dispose();
                this.removeChild(this.textEditor.editorContainer);
                this.textEditor = null;
            }
            return;
        }

        const editorProps: IEditorProps = this.getEditorProps(true);
        if (this.textEditor) {
            this.textEditor.updateEditorProps(editorProps);
        } else {
            this.textEditor = new TextEditor(editorProps, this.getTextServices());
            this.model.noteTextBody?.setEditorComponent(this.textEditor);
            this.addChild(this.textEditor.editorContainer);
        }
        this.model.noteInstance = this;
        this.updateNoteHeight();  // textEditor的高度可能大于容器高度，需要更新一下
    };

    private getEditorProps = (shouldUpdate): IEditorProps => ({
        shouldUpdate,
        paragraphList: this.model.noteTextBody?.paragraphList,
        textBodyStyle: this.model.noteTextBody?.textBodyStyle,
        modelKey: this.model.key,
        textRect: this.getTextRect(),
        selected: this.model.selected,
        editable: this.isEditable(),
        selectable: true,
        eventArea: EventArea.maxBlock,
        slideNum: getSlideIndex(this.model?.slideRef) + 1,
        datetime: this.getInfrastructureService().initDatetime,
        needRenderTextExtra: false,
        onEnterEditing: this.handleEnterEditing,
        onTextChange: this.handleTextChange,
        onExitEditing: this.handleExitEditing,
        onChange: this.handleChange,
        onStyleChange: this.handleEditorStyleChange,
        handleSelectionChange: this.handleSelectionChange,
        resetShouldUpdate: () => {
            this.model.shouldUpdate = false;
        },
        cursor: this.isEditable() ? 'text' : 'point',
        styleRenderCallback: this.handleStyleRenderCallback,
        getApplication: () => this.application,
        isNotFillInvisibleText: true,
        supportScrollMobile: this.editMode,
    });

    private getTextRect = () => {
        const { height, width } = this.application;
        return {
            height,
            width,
            left: 0,
            top: 0 - this.offsetTop,
        };
    };

    private handleChange = (...rest) => {
        this.updateData(...rest);
    };

    private updateData = ({ paragraphList, textBodyStyle } = { paragraphList: [], textBodyStyle: {} }) => {
        this.model?.updateParagraphList(paragraphList, textBodyStyle);
    };

    // textChange时需要更新备注高度
    private handleTextChange = (rect) => {
        // 触发锁页
        this.getAppServices().viewableServiceLayer.textEditorStateService.editorContentChangeFlag += 1;
        const height = this.getTextRectHeight(rect);
        if (height > this.containerHeight) {
            this.updateNoteHeight(height);
            this.scrollContainerIfHeightChange(height);
        }
    };

    // 参照 pc 逻辑更新备注高度
    private updateNoteHeight = (height?: number) => {
        // textRectHeight 可能为小数，需要向上取整与 applicationHeight 做对比
        const textRectHeight = height || this.getTextRectHeight();
        const applicationHeight = this.application.view.height;
        const expectHeight = this.props.isScrollMode ? Math.min(textRectHeight, MAX_NOTE_HEIGHT) : textRectHeight;
        if (!this.shouldUpdateNoteHeight(expectHeight, applicationHeight, this.containerHeight)) {
            return;
        }
        this.setApplicationHeight(Math.max(expectHeight, this.containerHeight));
        this.isLargeNote = textRectHeight > MAX_NOTE_HEIGHT;
        this.props.handleNoteHeightUpdated?.(textRectHeight, this.application.height);
        // height 变化时，更新 props 以修改 editor 的 rect
        this.updateEditorPropsWithoutValueChange();
    };

    private shouldUpdateNoteHeight(expectHeight: number, applicationHeight: number, containerHeight: number) {
        // 期望高度与当前备注高度相同，且期望备注高度大于备注容器高度
        if (expectHeight === applicationHeight && containerHeight < expectHeight) {
            return false;
        }
        // 期望高度小于当前备注高度，且当前备注高度与容器高度相同
        if (expectHeight < applicationHeight && containerHeight === applicationHeight) {
            return false;
        }
        return true;
    }

    private handleStyleRenderCallback = (style, type) => {
        const forceStyle: {
            bodyStyle?: any;
            textRunStyle?: any;
            [key: string]: any
        } = {
            bodyStyle: this.props.noteBodyStyle,
        };

        return styleRenderCallback(forceStyle)(style, type);
    };

    private handleSelectionChange = () => {
        this.updateContainerScroll();
        this.showMobileRightMenu(true);
    };

    /**
     * 滚动的计算内，所有的位置以canvas左上角为原点，由此解释以下变量
     * wrapperTop: 容器元素的scrollTop
     * wrapperBottom: 容器元素的高度+scrollTop
     * top: 选区顶部相对于canvas左上角的 y 值
     * bottom: 选区底部相对于canvas左上角的 y 值
     * cacheSelectionRect: 缓存上一次的选区
     */
    private updateContainerScroll = (toView?: boolean) => {
        const selectionRect = this.textEditor?.getExpandedRect();
        const noteWrapperElem = this.getContainerElem();
        if (selectionRect && noteWrapperElem) {
            selectionRect.top += this.offsetTop;
            selectionRect.bottom += this.offsetTop;
            const top = Math.floor(selectionRect.top);
            const bottom = Math.floor(selectionRect.bottom);
            const oldTop = this.cacheSelectionRect.top;
            const oldBottom = this.cacheSelectionRect.bottom;
            const wrapperTop = noteWrapperElem.scrollTop;
            const wrapperBottom = noteWrapperElem.clientHeight + noteWrapperElem.scrollTop;

            if (toView && (top > wrapperBottom || bottom < wrapperTop)) {
                const halfHeight = Math.floor((wrapperBottom - wrapperTop) / 2);
                noteWrapperElem.scrollTo(0, top - halfHeight);
            } else {
                this.activeScroll(bottom, oldBottom, wrapperBottom, noteWrapperElem, top, oldTop, wrapperTop);
            }
            this.cacheSelectionRect = { top, bottom };
        }
    };

    private activeScroll = (
        bottom: number, oldBottom: number, wrapperBottom: number,
        noteWrapperElem: HTMLElement, top: number, oldTop: number, wrapperTop: number,
    ) => {
        if (bottom > Math.max(oldBottom, wrapperBottom)) {
            // 向下滚动至底部选区
            noteWrapperElem.scrollTo(0, bottom - noteWrapperElem.clientHeight);
        } else if (top < Math.min(oldTop, wrapperTop)) {
            // 向上滚动至顶部选区
            if (bottom <= oldBottom) { // 避免 ctrl+a 全选时滚动到顶部
                noteWrapperElem.scrollTo(0, top);
            }
        } else if (bottom < Math.min(oldBottom, wrapperTop + LINE_HEIGHT)) {
            // 向上回滚至底部选区露出一行
            noteWrapperElem.scrollTo(0, bottom - LINE_HEIGHT);
        } else if (top > Math.max(oldTop, wrapperBottom - LINE_HEIGHT)) {
            // 向下回滚至顶部选区露出一行
            noteWrapperElem.scrollTo(0, top - noteWrapperElem.clientHeight + LINE_HEIGHT);
        }
    };

    private showMobileRightMenu = (useExpanded = false) => {
        if (this.mobileRightMenuManager) {
            this.mobileRightMenuManager.isClickOnElement = true;
        }
        if (this.selectionTimeout) {
            clearTimeout(this.selectionTimeout);
        }

        const isExpanded = this.textEditor?.isExpanded?.();
        if (!this.getEditorInstanceService().checkEditorIsEditing()
            || !this.isEditing || (useExpanded && !isExpanded)) {
            this.mobileRightMenuManager?.hideMenu();
            return;
        }

        this.selectionTimeout = setTimeout(() => {
            const textRect = this.textEditor?.getExpandedRect();
            if (!textRect) {
                return;
            }
            this.mobileRightMenuManager?.showNoteAreaMenu({
                left: textRect.left,
                top: textRect.top,
                right: textRect.right,
                bottom: textRect.bottom,
                offsetTop: this.offsetTop,
                container: this.getContainerElem(),
            });
        }, SHOW_RIGHT_MENU_DELAY);
    };

    private getContainerElem = (): HTMLDivElement => this.props.containerElement;

    private handleEditorStyleChange = (style) => {
        if (this.model.noteTextBody) {
            this.model.noteTextBody.selectionStyles = style.style;
        }
    };

    private handleEnterEditing = () => {
        this.isFocus = true;
        this.props.onEnterEditing?.();
    };

    private handleNoteDoubleTap = () => {
        this.props.onDoubleTap?.();
    };

    private renderPlaceholder() {
        if (!this.isShowPlaceHolder()) {
            if (this.placeHolderComponent) {
                this.placeHolderComponent.editorContainer.dispose();
                this.removeChild(this.placeHolderComponent.editorContainer);
                this.placeHolderComponent = null;
            }
            return;
        }
        const placeholderText = this.slideExistence() ? i18n.t('双击添加备注') : i18n.t('请先添加页面再添加备注');
        this.setApplicationHeight(this.containerHeight);
        const { placeholderBodyStyle } = this.props;
        const editorProps: IEditorProps = {
            shouldUpdate: true,
            paragraphList: getPlaceholderParagraphList(placeholderText) as [],
            modelKey: this.model?.key,
            textBodyStyle: { bodyStyle: placeholderBodyStyle },
            textRect: this.getTextRect(),
            selected: false,
            editable: false,
            eventArea: EventArea.maxBlock,
            getApplication: () => this.application,
        };
        if (this.placeHolderComponent) {
            this.placeHolderComponent.updateEditorProps(editorProps);
        } else {
            this.placeHolderComponent = new TextEditor(editorProps, this.getTextServices());
            this.addChild(this.placeHolderComponent.editorContainer);
        }
    }

    private handleExitEditing = () => {
        if (this.isFocus) {
            this.isFocus = false;
            if (this.isShowPlaceHolder()) {
                this.renderNote();
            }
        }
        this.props.onExitEditing?.();
    };

    private getTextRectHeight = (textRect?: { height: number, width: number}): number => {
        // 避免小数影响计算，取到数值后进行取整
        const height = Math.floor(textRect?.height || this.getTextAreaHeightSync());
        return height;
    };

    public get isEditing() {
        return !!this.textEditor?.isEditing;
    }

    private getAppServices(): IMobileEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IMobileEditorAppServices;
    }

    private getInfrastructureService(): IInfrastructureBase {
        return this.getAppServices().getInfrastructureService();
    }

    private getEditorInstanceService() {
        return this.getAppServices().viewableServiceLayer.editorInstanceService;
    }

    private setApplicationHeight(height) {
        const relHeight = Math.floor(height);
        if (height && this.application.height !== relHeight) {
            this.application.height = relHeight;
        }
    }
    private setApplicationWidth(width) {
        const relWidth = Math.floor(width);
        if (width && this.application.width !== relWidth) {
            this.application.width = relWidth;
        }
    }

    private getTextServices(): IEditorServices {
        return  {
            uiDataService: this.getAppServices().getUiDataService(),
            editingStateManager: this.getAppServices().viewableServiceLayer.editingStateManager,
            selectionService: this.getAppServices().viewableServiceLayer.selectionService,
            fontManager: this.getAppServices().viewableServiceLayer.fontManager,
            textEditorStateService: this.getAppServices().viewableServiceLayer.textEditorStateService,
            accessibilityManager: this.getAppServices().viewableServiceLayer.accessibilityManager,
            undoRedoService: this.getAppServices().editableServiceLayer?.undoRedoService,
            linkManager: this.getAppServices().viewableServiceLayer.linkManager,
            logicService: new NoteEditLogicService(),
        };
    }
}
